/**
 * GeIcon — set d'icônes maison « Le Fil d'Or » (DESIGN.md §7).
 *
 * Icônes SIGNIFIANTES, pas génériques : chaque pictogramme raconte le sens
 * sénégalais du geste (case à toit pointu, tama qui annonce la nouvelle,
 * fil dans le chas de l'aiguille, étoile du drapeau, boubou…).
 *
 * Règles :
 *  - SVG stroke 2px, terminaisons rondes, grille 24×24, `currentColor`
 *    (thémable clair/nuit automatiquement), taille `1em` (suit le font-size
 *    du parent — .bn-icon, .sr-icon, etc. restent inchangés).
 *  - Le trait « point de couture » (dasharray) code une info vraie :
 *    validé = cousu, fil de l'aide, etc. Jamais purement décoratif.
 *  - Fallback : un `name` inconnu est rendu tel quel (emoji hérité) — la
 *    migration peut se faire registre par registre sans rien casser.
 */

const P = {
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  fill: 'none',
};

export const GE_ICONS = {
  /* La case — l'accueil, le toit sous lequel on revient. */
  accueil: (
    <>
      <path {...P} d="M3.5 11 12 3.5 20.5 11" />
      <path {...P} d="M6 11v8.5h12V11" />
      <path {...P} d="M10.5 19.5v-4a1.5 1.5 0 0 1 3 0v4" />
    </>
  ),
  /* Deux personnes côte à côte — la communauté qu'on accompagne. */
  beneficiaires: (
    <>
      <circle {...P} cx="9" cy="8" r="3.25" />
      <path {...P} d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
      <path {...P} d="M15.5 12.4a3.25 3.25 0 1 0-1.6-6.1" />
      <path {...P} d="M16.4 14.6a5.5 5.5 0 0 1 4.1 4.9" />
    </>
  ),
  /* Le fil entre dans le chas de l'aiguille — enrôler, faire entrer
     quelqu'un dans le parcours. */
  enroler: (
    <>
      <path {...P} d="M4 20 15.6 8.4" />
      <ellipse {...P} cx="17.6" cy="6.4" rx="2.3" ry="2.3" />
      <path {...P} strokeDasharray="2.5 3" d="M19.5 8.2c2.2 2.6-2.8 3.6-1.2 6.3" />
    </>
  ),
  /* Le tama (tambour parleur) — c'est lui qui annonce la nouvelle. */
  notifications: (
    <>
      <path {...P} d="M8 5h8M8 19h8" />
      <path {...P} d="M8 5c2 3.2 2 10.8 0 14M16 5c-2 3.2-2 10.8 0 14" />
      <path {...P} d="M18.5 8.5c1 1 1 2 .8 3M20.6 6.9c1.6 1.6 1.7 3.5 1.2 5" strokeWidth="1.6" />
    </>
  ),
  /* Le boubou — la personne, avec l'ampleur du vêtement de dignité. */
  profil: (
    <>
      <circle {...P} cx="12" cy="6" r="3" />
      <path {...P} d="M4.5 20c1-5.5 4-9 7.5-9s6.5 3.5 7.5 9" />
      <path {...P} d="M4.5 20h15" />
    </>
  ),
  /* Le drapeau qui ondule — la campagne de mobilisation. */
  campagnes: (
    <>
      <path {...P} d="M5 3v18" />
      <path {...P} d="M5 4.5c2.7-1.5 4.6 1.2 7 .5 2-.6 3.5-1.5 7-.5v8c-3.5-1-5 0-7 .5-2.4.7-4.3-2-7-.5" />
    </>
  ),
  /* L'étoile du drapeau, brodée — le classement, l'excellence. */
  classement: (
    <>
      <path {...P} d="M12 3.5l2.4 5.1 5.6.8-4 3.9 1 5.6-5-2.7-5 2.7 1-5.6-4-3.9 5.6-.8z" />
    </>
  ),
  /* La coche cousue au fil — validé = cousu dans le registre. */
  validation: (
    <>
      <path {...P} d="M4.5 12.5 10 18 19.5 6.5" strokeDasharray="4 3" />
    </>
  ),
  /* Le document au liseré tissé — les rapports. */
  rapports: (
    <>
      <path {...P} d="M6 3.5h9L19 7.5v13H6z" />
      <path {...P} d="M15 3.5v4h4" />
      <path {...P} d="M9 12.5h7" />
      <path {...P} strokeDasharray="2.5 2.5" d="M9 16h7" />
    </>
  ),
  /* La pièce au-dessus de la main ouverte — les fonds : l'app témoin du
     geste, jamais banque (CHARTE_ARGENT). */
  fonds: (
    <>
      <circle {...P} cx="12" cy="7.5" r="3.6" />
      <path {...P} strokeWidth="1.6" d="M12 5.9v3.2M10.7 7.5h2.6" />
      <path {...P} d="M3.5 16.5c2 2.6 5 3.9 8.5 3.9s6.5-1.3 8.5-3.9" />
      <path {...P} d="M3.5 16.5v3M20.5 16.5v3" />
    </>
  ),
  /* Deux bulles qui se répondent — l'interaction Relais ↔ Bénéficiaire. */
  interaction: (
    <>
      <path {...P} d="M4 5.5h9.5a1.8 1.8 0 0 1 1.8 1.8v4a1.8 1.8 0 0 1-1.8 1.8H8.5L5 16v-3H4a1.8 1.8 0 0 1-1.8-1.8v-4A1.8 1.8 0 0 1 4 5.5z" />
      <path {...P} strokeDasharray="2.5 2.5" d="M18.5 9.5h1.5a1.8 1.8 0 0 1 1.8 1.8v3.6a1.8 1.8 0 0 1-1.8 1.8h-.6v2.8l-3.2-2.8h-4" />
    </>
  ),
  /* Le croissant sous lequel brille le fil — Mode Nuit. */
  lune: (
    <>
      <path {...P} d="M13.8 3.6a8.4 8.4 0 1 0 6.6 13.2A9.2 9.2 0 0 1 13.8 3.6z" />
      <path {...P} strokeWidth="1.6" d="M16.5 8.2v.01M18.8 11.4v.01" />
    </>
  ),
  /* Le soleil du terrain — Mode Clair. */
  soleil: (
    <>
      <circle {...P} cx="12" cy="12" r="4.2" />
      <path {...P} d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7" />
    </>
  ),
  /* Le cœur à moitié cousu — l'aide qui complète le fil. */
  aide: (
    <>
      <path {...P} d="M12 20C5.6 14.6 3.6 10 6.4 7.2 8.7 5 11.5 6.8 12 9" />
      <path {...P} strokeDasharray="3 2.6" d="M12 20c6.4-5.4 8.4-10 5.6-12.8C15.3 5 12.5 6.8 12 9" />
    </>
  ),
  /* Flèche qui revient sur le fil — Précédent (wizard / questionnaire).
     Le trait pointillé du fût = on rebrousse le parcours, pas une flèche générique. */
  precedent: (
    <>
      <path {...P} d="M11 6 5 12l6 6" />
      <path {...P} strokeDasharray="3 2.5" d="M5 12h14" />
    </>
  ),
};

/* Alias sémantiques (même géométrie, sens partagé). */
GE_ICONS.utilisateurs = GE_ICONS.beneficiaires;

function GeIcon({ name, size = '1em', className = '', title }) {
  const paths = GE_ICONS[name];
  if (!paths) {
    // Nom inconnu (emoji hérité, migration progressive) — rendu tel quel.
    return <span className={className} aria-hidden={title ? undefined : true}>{name}</span>;
  }
  return (
    <svg
      className={`ge-icon${className ? ` ${className}` : ''}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {paths}
    </svg>
  );
}

export default GeIcon;
